import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';

import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';

import { UserTeamSelector } from '../../UserTeamSelector';

import { AccessListDatatable } from './AccessListDatatable';
import { NamespaceSelect } from './NamespaceSelect';
import { KubernetesRbacFormValues, KubernetesUnifiedAccess } from './types';

// Namespaced roles that require namespace specification
const NAMESPACE_ROLE_IDS = [3, 4, 6]; // Standard, Read-only, Namespace Operator

interface KubernetesRBACConstraintsProps {
  formData: KubernetesRbacFormValues;
  onChange: (key: 'accessList', value: KubernetesUnifiedAccess[]) => void;
}

type UserTeamValue = {
  Type: 'user' | 'team';
  Id: number;
  Name: string;
};

export function KubernetesRBACFormFields({
  formData,
  onChange,
}: KubernetesRBACConstraintsProps) {
  const [tempSelectedRole, setTempSelectedRole] = useState<number>(0);
  const [tempSelectedUsersAndTeams, setTempSelectedUsersAndTeams] = useState<
    UserTeamValue[]
  >([]);
  const [tempSelectedNamespaces, setTempSelectedNamespaces] = useState<
    string[]
  >([]);

  const userOptionsQuery = useUsers(false, undefined, true, (users) =>
    users.map((user) => ({ value: user.Id, label: user.Username }))
  );
  const teamOptionsQuery = useTeams(false, undefined, {
    select: (teams) => teams.map((t) => ({ value: t.Id, label: t.Name })),
  });
  const roleOptionsQuery = useRbacRoles({
    select: (roles) =>
      roles
        .filter((role) => role.Scope && role.Scope.kubernetes)
        .map((role) => ({ value: role.Id, label: role.Name })),
  });

  const userOptions = userOptionsQuery.data || [];
  const teamOptions = teamOptionsQuery.data || [];
  const roleOptions = roleOptionsQuery.data || [];

  const accessList = formData.accessList || [];

  // Combine users and teams into one list with proper structure
  const usersAndTeamsOptions = [
    ...userOptions.map((u) => ({
      label: u.label,
      value: { Type: 'user' as const, Id: u.value, Name: u.label },
    })),
    ...teamOptions.map((t) => ({
      label: t.label,
      value: { Type: 'team' as const, Id: t.value, Name: t.label },
    })),
  ];

  // Get the selected role name
  const selectedRoleName = useMemo(() => {
    const role = roleOptionsQuery.data?.find(
      (r) => r.value === tempSelectedRole
    );
    return role?.label || '';
  }, [tempSelectedRole, roleOptionsQuery.data]);

  // Check if selected role requires namespace specification
  const isNamespaceRole = NAMESPACE_ROLE_IDS.includes(tempSelectedRole);

  // Check if the Add button should be enabled
  const canAddAccess =
    tempSelectedUsersAndTeams.length > 0 &&
    tempSelectedRole > 0 &&
    (!isNamespaceRole || tempSelectedNamespaces.length > 0);

  return (
    <>
      <FormSection title="Add access" className="mb-8">
        <FormControl label="Users/teams" required>
          <UserTeamSelector
            options={usersAndTeamsOptions}
            value={tempSelectedUsersAndTeams}
            onChange={setTempSelectedUsersAndTeams}
          />
        </FormControl>

        <FormControl label="Role" required>
          <PortainerSelect<number>
            value={tempSelectedRole}
            onChange={(value) => setTempSelectedRole(value || 0)}
            options={roleOptions}
            placeholder="Select a role"
            bindToBody
            data-cy="rbac-role-select"
          />
        </FormControl>

        {/* Show namespace input for namespace-scoped roles */}
        {isNamespaceRole && (
          <FormControl label="Namespaces" required>
            <p className="mb-2 text-xs text-muted">
              The <strong>{selectedRoleName}</strong> role is namespace-scoped.
              Select the namespaces that the user(s) / team(s) should have
              access to:
            </p>
            <NamespaceSelect
              selectedNamespaces={tempSelectedNamespaces}
              onChange={setTempSelectedNamespaces}
              data-cy="rbac-namespaces-input"
              namespaces={getNamespacesFromAccessList()}
            />
          </FormControl>
        )}

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      {/* Access List Datatable */}
      <div className="-mx-4">
        <AccessListDatatable
          formData={formData}
          onChange={onChange}
          roleOptions={roleOptions}
        />
      </div>

      {/* Access Info message */}
      <div className="form-group">
        <div className="col-sm-12">
          {accessList.length > 0 && (
            <TextTip color="blue">
              {(() => {
                const userCount = accessList.filter(
                  (a) => a.Type === 'user'
                ).length;
                const teamCount = accessList.filter(
                  (a) => a.Type === 'team'
                ).length;
                const envAccessCount = accessList.filter(
                  (a) => !NAMESPACE_ROLE_IDS.includes(a.Role.Id)
                ).length;
                const namespaceAccessCount = accessList.filter((a) =>
                  NAMESPACE_ROLE_IDS.includes(a.Role.Id)
                ).length;

                return `Access configured for ${userCount} user(s) and ${teamCount} team(s) (${envAccessCount} environment access, ${namespaceAccessCount} namespace access).`;
              })()}
            </TextTip>
          )}
        </div>
      </div>
    </>
  );

  function handleAddAccess() {
    if (!canAddAccess) return;

    const namespaces = cleanNamespaces(tempSelectedNamespaces);
    let newAccessList = [...accessList];

    newAccessList = tempSelectedUsersAndTeams.reduce((result, selection) => {
      if (selection.Type === 'user') {
        return addOrUpdateUser(result, selection.Id, namespaces);
      }
      if (selection.Type === 'team') {
        return addOrUpdateTeam(result, selection.Id, namespaces);
      }

      return result;
    }, newAccessList);

    onChange('accessList', newAccessList);
    resetSelections();
  }

  function addOrUpdateUser(
    accessList: KubernetesUnifiedAccess[],
    userId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess[] {
    const userEntry = createUserAccess(userId, namespaces);
    if (!userEntry) return accessList;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'user' && a.Id === userId
    );

    if (existingIndex === -1) {
      return [...accessList, userEntry];
    }

    const updatedEntry = { ...accessList[existingIndex], ...userEntry };
    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, updatedEntry, ...afterEntry];
  }

  function addOrUpdateTeam(
    accessList: KubernetesUnifiedAccess[],
    teamId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess[] {
    const teamEntry = createTeamAccess(teamId, namespaces);
    if (!teamEntry) return accessList;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'team' && a.Id === teamId
    );

    if (existingIndex === -1) {
      return [...accessList, teamEntry];
    }

    const updatedEntry = { ...accessList[existingIndex], ...teamEntry };
    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, updatedEntry, ...afterEntry];
  }

  function createUserAccess(
    userId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess | null {
    const user = userOptions.find((u) => u.value === userId);
    if (!user) return null;

    return {
      Id: userId,
      Type: 'user',
      Name: user.label,
      Role: { Id: tempSelectedRole, Name: selectedRoleName },
      namespaces: isNamespaceRole ? namespaces : undefined,
    };
  }

  function createTeamAccess(
    teamId: number,
    namespaces: string[]
  ): KubernetesUnifiedAccess | null {
    const team = teamOptions.find((t) => t.value === teamId);
    if (!team) return null;

    return {
      Id: teamId,
      Type: 'team',
      Name: team.label,
      Role: { Id: tempSelectedRole, Name: selectedRoleName },
      namespaces: isNamespaceRole ? namespaces : undefined,
    };
  }

  function cleanNamespaces(input: string[]): string[] {
    return input.map((ns) => ns.trim()).filter((ns) => ns !== '');
  }

  function resetSelections() {
    setTempSelectedUsersAndTeams([]);
    setTempSelectedNamespaces([]);
  }

  function getNamespacesFromAccessList(): string[] {
    return Array.from(new Set(accessList.flatMap((a) => a.namespaces || [])));
  }
}
